import React, { useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { EventBody, EventCost, EventDetailDTO, EventType } from "../../../../models/EventDTO";
import S from "./strings";
import { Modal, aInput, deInput, errorText, numOrNull } from "../workshops/comun";
import { FechaHoraDMY } from "../../../../components/fechas/camposFecha";

interface Props {
	campusId: number;
	evento: EventDetailDTO | null;
	onClose: () => void;
	onSaved: (e: EventDetailDTO, texto: string) => void;
}

const TIPOS: EventType[] = ["graduacion", "batalla", "seminario", "otro"];
const txt = (v: any) => (v === null || v === undefined ? "" : String(v));

/** Crear / editar un evento con sus precios y costos. */
const EventForm: React.FC<Props> = ({ campusId, evento, onClose, onSaved }) => {
	const http = HttpManager.getInstance();
	const [f, setF] = useState({
		type: (evento?.type || "graduacion") as EventType,
		name: evento?.name || "",
		date: aInput(evento?.dateTime || evento?.date),
		venue: evento?.venue || "",
		capacity: txt(evento?.capacity),
		tableSize: txt(evento?.tableSize),
		packagePrice: txt(evento?.packagePrice),
		extraGuestPrice: txt(evento?.extraGuestPrice),
		childPrice: txt(evento?.childPrice),
		notes: evento?.notes || "",
		status: (evento?.status === "historico" ? "programado" : evento?.status || "programado") as "programado" | "realizado" | "anulado"
	});
	const [costs, setCosts] = useState<{ concept: string; amount: string }[]>((evento?.costs || []).map(c => ({ concept: c.concept, amount: String(c.amount) })));
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const set = (k: keyof typeof f, v: any) => setF(x => ({ ...x, [k]: v }));
	const valido = f.name.trim().length > 1 && f.date !== "";

	const guardar = () => {
		const limpios: EventCost[] = costs.filter(c => c.concept.trim() && c.amount.trim() !== "" && !isNaN(Number(c.amount))).map(c => ({ concept: c.concept.trim(), amount: Number(c.amount) }));
		const body: EventBody = {
			type: f.type, name: f.name.trim(), date: deInput(f.date) as string, venue: f.venue.trim() || null,
			capacity: numOrNull(f.capacity), tableSize: numOrNull(f.tableSize), packagePrice: numOrNull(f.packagePrice),
			extraGuestPrice: numOrNull(f.extraGuestPrice), childPrice: numOrNull(f.childPrice), costs: limpios, notes: f.notes.trim() || null
		};
		setBusy(true); setError(null);
		const req = evento ? http.eventsService.update(campusId, { ...body, id: evento.id, status: f.status }) : http.eventsService.create(campusId, body);
		req.then(r => onSaved(r.data, evento ? S.guardado : S.creado)).catch(e => { setError(errorText(e)); setBusy(false); });
	};

	const campo = (k: keyof typeof f, label: string, tipo = "number", flex = "1 1 130px") => (
		<label className="wau-campo" style={{ flex }}><span className="wau-lab">{label}</span>
			<input type={tipo} min={0} step={tipo === "number" ? "0.01" : undefined} className={"wau-entrada" + (tipo === "number" ? " wau-num" : "")} value={f[k] as string} onChange={e => set(k, e.target.value)} /></label>
	);

	return (
		<Modal title={evento ? S.editar + " · " + evento.name : S.nuevo} onClose={onClose} ancho={680}>
			<div className="wau-fila" style={{ marginBottom: 10 }}>
				<label className="wau-campo" style={{ flex: "1 1 150px" }}><span className="wau-lab">{S.tipo}</span>
					<select className="wau-entrada" value={f.type} onChange={e => set("type", e.target.value)}>
						{TIPOS.map(t => <option key={t} value={t}>{S.tipos[t]}</option>)}
					</select></label>
				<label className="wau-campo" style={{ flex: "3 1 260px" }}><span className="wau-lab">{S.nombre}</span>
					<input className="wau-entrada" value={f.name} onChange={e => set("name", e.target.value)} autoFocus /></label>
			</div>
			<div className="wau-fila" style={{ marginBottom: 10 }}>
				<div className="wau-campo" style={{ flex: "1 1 260px" }}><span className="wau-lab">{S.fecha}</span>
					<FechaHoraDMY value={f.date} onChange={v => set("date", v)} /></div>
				{campo("venue", S.lugar, "text", "2 1 220px")}
				{evento ? (
					<label className="wau-campo" style={{ flex: "1 1 140px" }}><span className="wau-lab">{S.estado}</span>
						<select className="wau-entrada" value={f.status} onChange={e => set("status", e.target.value)}>
							<option value="programado">{S.estados.programado}</option>
							<option value="realizado">{S.estados.realizado}</option>
							<option value="anulado">{S.estados.anulado}</option>
						</select></label>
				) : null}
			</div>
			<div className="wau-fila" style={{ marginBottom: 10 }}>
				{campo("capacity", S.cupo, "number", "1 1 100px")}
				{campo("tableSize", S.porMesa, "number", "1 1 130px")}
				{campo("packagePrice", S.paquete)}
				{campo("extraGuestPrice", S.invitadoExtra)}
				{campo("childPrice", S.nino, "number", "1 1 100px")}
			</div>
			<span className="wau-lab">{S.costos}</span>
			<div style={{ marginTop: 4, marginBottom: 10 }}>
				{costs.map((c, i) => (
					<div key={i} className="wte-costo">
						<input className="wau-entrada" aria-label={S.concepto} placeholder={S.concepto} value={c.concept} onChange={e => setCosts(cs => cs.map((x, j) => j === i ? { ...x, concept: e.target.value } : x))} />
						<input type="number" min={0} step="0.01" className="wau-entrada wau-num" aria-label={S.monto} placeholder={S.monto} value={c.amount} onChange={e => setCosts(cs => cs.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} />
						<button type="button" className="wau-btn s mini" aria-label={S.quitar} onClick={() => setCosts(cs => cs.filter((_x, j) => j !== i))}>×</button>
					</div>
				))}
				<button type="button" className="wau-btn s mini" onClick={() => setCosts(cs => [...cs, { concept: "", amount: "" }])}>{S.agregarCosto}</button>
			</div>
			<label className="wau-campo"><span className="wau-lab">{S.notas}</span>
				<textarea className="wau-entrada" rows={2} value={f.notes} onChange={e => set("notes", e.target.value)} /></label>
			{error ? <p className="wau-adv" style={{ marginTop: 8 }}>{error}</p> : null}
			<div className="wau-acciones">
				<button type="button" className="wau-btn s" onClick={onClose} disabled={busy}>{S.volver}</button>
				<button type="button" className="wau-btn p" onClick={guardar} disabled={busy || !valido}>{evento ? S.guardar : S.crear}</button>
			</div>
		</Modal>
	);
};

export default EventForm;
